import { toast, ToastOptions, Bounce } from 'react-toastify';

// Configurações padrão para toasts
const defaultOptions: ToastOptions = {
  position: 'bottom-right',
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  transition: Bounce,
};

// Configurações específicas por tipo
const successOptions: ToastOptions = {
  ...defaultOptions,
  className: 'toast-success',
  progressClassName: 'toast-progress-success',
};

const errorOptions: ToastOptions = {
  ...defaultOptions,
  autoClose: 4000, // Erros permanecem mais tempo
  className: 'toast-error',
  progressClassName: 'toast-progress-error',
};

const warningOptions: ToastOptions = {
  ...defaultOptions,
  className: 'toast-warning',
  progressClassName: 'toast-progress-warning',
};

const infoOptions: ToastOptions = {
  ...defaultOptions,
  className: 'toast-info',
  progressClassName: 'toast-progress-info',
};

/**
 * Notificação de sucesso
 * @param message Mensagem a exibir
 * @param options Opções customizadas
 */
export const showSuccess = (message: string, options?: Partial<ToastOptions>) => {
  toast.success(message, { ...successOptions, ...options });
};

/**
 * Notificação de erro
 * @param message Mensagem a exibir
 * @param options Opções customizadas
 */
export const showError = (message: string, options?: Partial<ToastOptions>) => {
  toast.error(message, { ...errorOptions, ...options });
};

/**
 * Notificação de aviso
 * @param message Mensagem a exibir
 * @param options Opções customizadas
 */
export const showWarning = (message: string, options?: Partial<ToastOptions>) => {
  toast.warning(message, { ...warningOptions, ...options });
};

/**
 * Notificação informativa
 * @param message Mensagem a exibir
 * @param options Opções customizadas
 */
export const showInfo = (message: string, options?: Partial<ToastOptions>) => {
  toast.info(message, { ...infoOptions, ...options });
};

/**
 * Notificação customizada
 * @param message Mensagem a exibir
 * @param type Tipo de notificação
 * @param options Opções customizadas
 */
export const showToast = (
  message: string,
  type: 'success' | 'error' | 'warning' | 'info' = 'info',
  options?: Partial<ToastOptions>
) => {
  switch (type) {
    case 'success':
      showSuccess(message, options);
      break;
    case 'error':
      showError(message, options);
      break;
    case 'warning':
      showWarning(message, options);
      break;
    case 'info':
    default:
      showInfo(message, options);
  }
};

/**
 * Notificação com ação de desfazer (para futuro)
 * @param message Mensagem a exibir
 * @param onUndo Função chamada ao clicar desfazer
 */
export const showUndoToast = (
  message: string,
  onUndo: () => void,
  options?: Partial<ToastOptions>
) => {
  toast.success(
    `${message} - Clique para desfazer`,
    { ...successOptions, ...options }
  );
};

/**
 * Limpar todos os toasts
 */
export const dismissAll = () => {
  toast.dismiss();
};

/**
 * Limpar um toast específico
 * @param toastId ID do toast
 */
export const dismissToast = (toastId: string | number) => {
  toast.dismiss(toastId);
};

/**
 * Mostrar loading toast
 * @param message Mensagem a exibir
 * @returns ID do toast para posterior atualização
 */
export const showLoadingToast = (message: string) => {
  return toast.loading(`⏳ ${message}`, {
    ...defaultOptions,
    autoClose: false,
  });
};

/**
 * Atualizar um toast existente
 * @param toastId ID do toast anterior
 * @param message Nova mensagem
 * @param type Tipo de notificação (success, error, warning, info)
 */
export const updateToast = (
  toastId: string | number,
  message: string,
  type: 'success' | 'error' | 'warning' | 'info' = 'info'
) => {
  const options = {
    success: successOptions,
    error: errorOptions,
    warning: warningOptions,
    info: infoOptions,
  }[type];

  toast.update(toastId as any, {
    render: message,
    type,
    isLoading: false,
    ...options,
  });
};

const toastModule = {
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  toast: showToast,
  undo: showUndoToast,
  dismiss: dismissAll,
  loading: showLoadingToast,
  update: updateToast,
};

export default toastModule;
